import { Button, Col, message, Table, Row, TableProps, Grid } from 'antd';
import { SorterResult } from 'antd/lib/table/interface';
import config from 'config';
import { Routes } from 'config/routes';
import {
  useGetUsersListQuery,
  useRemoveUserMutation,
  UserRole,
  UserOutput,
  UserListSort,
  Order,
  UsersListFilterInput,
  PermissionsEnum,
} from 'gql/types';
import {
  createInternalUsersChipsConfig,
  excludedFilterFields,
} from 'modules/admin/configs/internalUsersFilters';
import { Spin, TableSearchInput } from 'modules/common/components';
import { FilterChips } from 'modules/common/components/FilterChips';
import { useSort, useDebounce, useFilter } from 'modules/common/hooks';
import { usePagination } from 'modules/navigation/hooks/usePagination';
import { useCurrentUser } from 'modules/user/hooks/useCurrentUser';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { getColumns } from './columns';
import styles from './styles.module.scss';

const { AddUsers, DeleteUsers, UpdateUserInfo } = PermissionsEnum;

const { useBreakpoint } = Grid;
const USER_ROLES_TO_DISPLAY = Object.values(UserRole).filter(
  (role) => role !== UserRole.Owner
);

const InternalUsersTable = () => {
  const { t } = useTranslation('admin.UsersListPage');

  const currentUser = useCurrentUser();
  const currentUserId = currentUser.data?.id;
  const canInvite = currentUser.isCan(AddUsers);
  const canDelete = currentUser.isCan(DeleteUsers);
  const canUpdateUserInfo = currentUser.isCan(UpdateUserInfo);

  const navigate = useNavigate();
  const pagination = usePagination();
  const sort = useSort<UserListSort>();

  const filter = useFilter<UsersListFilterInput>();

  const chipsConfig = useMemo(() => createInternalUsersChipsConfig(t), [t]);

  const currentFilter = filter.getFilterObject();

  const finalFilter = useMemo(() => {
    const roles = currentFilter.roles?.length
      ? currentFilter.roles
      : USER_ROLES_TO_DISPLAY;

    return {
      ...currentFilter,
      roles,
    };
  }, [currentFilter]);

  const getUsersListQuery = useGetUsersListQuery({
    variables: {
      input: {
        filter: finalFilter,
        take: config.PAGINATION.DEFAULT_LIMIT,
        skip: pagination.skip,
        sort: sort.options,
      },
    },
    onCompleted: (data) => {
      pagination.setTotal(data.getUsersList.meta.total);
    },
    onError: (error) => {
      message.error(error.message);
    },
  });

  const isLoading = getUsersListQuery.loading;
  const users = getUsersListQuery.data?.getUsersList.data as UserOutput[];

  const inviteOnClickHandler = () => {
    navigate(Routes.InviteUsers);
  };

  const navigateToEditUserPage = (userId: string) => {
    navigate(`${Routes.Users}/${userId}`);
  };

  const [removeUser, removeUserMutation] = useRemoveUserMutation({
    update(cache) {
      cache.evict({ fieldName: 'getUsersList' });
    },
    onCompleted: () => {
      message.success(t('successfullyDeleted'));
    },
    onError: (error) => message.error(error.message),
  });

  const deleteUserHandler = (id: string) => {
    removeUser({ variables: { input: { id } } });
  };

  const { md } = useBreakpoint();

  const columns = getColumns({
    onClickEditIcon: navigateToEditUserPage,
    deleteHandler: deleteUserHandler,
    canDelete,
    canUpdateUserInfo,
    currentUserId,
    showSorterTooltip: md,
    sort: sort?.options[0],
    filterValue: finalFilter,
  });

  const { debounce } = useDebounce();
  const handleSearch = (value: string) => {
    pagination.moveTo(1);
    filter.setFilterOption({
      fieldName: 'searchingQuery',
      value: value || undefined,
    });
  };

  const onTableChange: TableProps<UserOutput>['onChange'] = (
    _,
    filters,
    sorter,
    extra
  ) => {
    if (extra.action === 'sort') {
      const tableSorter = sorter as SorterResult<UserOutput>;
      const fieldName = tableSorter.columnKey as UserListSort;
      const order = tableSorter.order === 'ascend' ? Order.Asc : Order.Desc;
      if (fieldName && order && tableSorter.order) {
        sort.setSortOption({ fieldName, order });
      }
      if (!(sorter as any).order) {
        sort.removeSortOption(fieldName);
      }
    }
    if (extra.action === 'filter') {
      const roles = filters.role as UserRole[] | null;

      filter.setFilterOption({
        fieldName: 'roles',
        value: roles,
      });
    }
  };

  return (
    <Spin spinning={isLoading || removeUserMutation.loading}>
      <Row gutter={[0, 16]}>
        <Col span={24}>
          <Row gutter={[16, 10]} justify="space-between" align="middle">
            <Col flex={1}>
              <TableSearchInput
                onSearchChange={debounce(handleSearch)}
                placeholder={t('searchUser')}
                maxLength={100}
              />
            </Col>
            {canInvite && (
              <Col>
                <Button onClick={inviteOnClickHandler} type="primary">
                  {t('inviteUser')}
                </Button>
              </Col>
            )}
          </Row>
        </Col>

        <Col span={24}>
          <FilterChips<UsersListFilterInput>
            filterHook={filter}
            config={chipsConfig}
            excludeFields={excludedFilterFields}
          />
        </Col>

        <Col span={24}>
          <Table
            rowKey={({ id }) => id}
            rowClassName={({ id }) =>
              id === currentUserId ? styles.disabled : ''
            }
            columns={columns}
            dataSource={users}
            onChange={onTableChange}
            scroll={{ x: 1050 }}
            pagination={pagination.config}
            tableLayout="fixed"
          />
        </Col>
      </Row>
    </Spin>
  );
};

export default InternalUsersTable;
